import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  ChartLine, 
  Plus, 
  Trash, 
  ArrowUp, 
  ArrowDown, 
  Minus,
  MagnifyingGlass,
  Trophy,
  Target,
  TrendUp,
  Lightning,
  ArrowsClockwise,
  CurrencyDollar,
  Users,
  Globe,
  ArrowLeft
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import AppLayout from '@/components/AppLayout';
import PageTransition from '@/components/PageTransition';
import BackButton from '@/components/BackButton';
import { SkeletonList } from '@/components/ui/skeleton-loader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTheme } from '@/contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PositionBadge({ position }) {
  let bgColor = 'bg-red-500/20 text-red-400';
  if (position <= 3) bgColor = 'bg-yellow-500/20 text-yellow-400';
  else if (position <= 10) bgColor = 'bg-green-500/20 text-green-400';
  else if (position <= 30) bgColor = 'bg-blue-500/20 text-blue-400';
  
  return (
    <span className={`px-3 py-1 rounded-full text-sm font-bold ${bgColor}`}>
      #{position}
    </span>
  );
}

function EvolutionIndicator({ current, previous }) {
  const diff = previous - current;
  
  if (diff > 0) {
    return (
      <span className="flex items-center gap-1 text-green-400 text-sm">
        <ArrowUp weight="bold" />
        +{diff}
      </span>
    );
  } else if (diff < 0) {
    return (
      <span className="flex items-center gap-1 text-red-400 text-sm">
        <ArrowDown weight="bold" />
        {diff}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-muted-foreground text-sm">
      <Minus />
      =
    </span>
  );
}

function MiniChart({ history }) {
  if (!history || history.length < 2) return null;
  
  const positions = history.slice(-14).map(h => h.position);
  const min = Math.min(...positions);
  const max = Math.max(...positions);
  const range = max - min || 1;
  
  return (
    <div className="flex items-end gap-0.5 h-8">
      {positions.map((pos, i) => {
        const height = ((max - pos) / range) * 100;
        const isLast = i === positions.length - 1;
        return (
          <div
            key={i}
            className={`w-1.5 rounded-full transition-all ${
              isLast ? 'bg-primary' : 'bg-white/20'
            }`}
            style={{ height: `${Math.max(height, 10)}%` }}
          />
        );
      })}
    </div>
  );
}

export default function Ranking() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [keywords, setKeywords] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [newKeyword, setNewKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [addingKeyword, setAddingKeyword] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [trackingStatus, setTrackingStatus] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);

  useEffect(() => {
    fetchKeywords();
    fetchDashboard();
    fetchTrackingStatus();
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true,
      });
      setWebsiteUrl(response.data.website_url || '');
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchTrackingStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ranking/status`, {
        withCredentials: true,
      });
      setTrackingStatus(response.data);
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const saveWebsiteUrl = async () => {
    setSavingUrl(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/auth/profile`,
        { website_url: websiteUrl },
        { withCredentials: true }
      );
      toast.success(t('ranking.urlSaved'));
    } catch (error) {
      toast.error(t('errors.savingError'));
    } finally {
      setSavingUrl(false);
    }
  };

  const fetchKeywords = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ranking/keywords`, {
        withCredentials: true,
      });
      setKeywords(response.data.keywords);
    } catch (error) {
      console.error('Error fetching keywords:', error);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ranking/dashboard`, {
        withCredentials: true,
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const addKeyword = async () => {
    if (!newKeyword.trim()) return;
    
    setAddingKeyword(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/ranking/keywords`,
        { keyword: newKeyword },
        { withCredentials: true }
      );
      toast.success(t('ranking.keywordAdded'));
      setNewKeyword('');
      setAnalysis(null);
      fetchKeywords();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generationError'));
    } finally {
      setAddingKeyword(false);
    }
  };

  const analyzeKeyword = async () => {
    if (!newKeyword.trim()) return;
    
    setAnalyzing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ranking/analyze`,
        { keyword: newKeyword },
        { withCredentials: true }
      );
      setAnalysis(response.data);
    } catch (error) {
      toast.error(t('errors.generationError'));
    } finally {
      setAnalyzing(false);
    }
  };

  const refreshKeyword = async (keywordId) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ranking/keywords/${keywordId}/refresh`,
        {},
        { withCredentials: true }
      );
      toast.success(`${t('ranking.positionUpdated')} : #${response.data.current_position}`);
      fetchKeywords();
      fetchDashboard();
    } catch (error) {
      toast.error(t('errors.generationError'));
    }
  };

  const deleteKeyword = async (keywordId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/ranking/keywords/${keywordId}`, {
        withCredentials: true,
      });
      toast.success(t('common.delete'));
      fetchKeywords();
      fetchDashboard();
    } catch (error) {
      toast.error(t('errors.deletionError'));
    }
  };

  const viewKeywordDetails = async (keywordId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/ranking/keywords/${keywordId}`,
        { withCredentials: true }
      );
      setSelectedKeyword(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  return (
    <AppLayout>
      <PageTransition>
        <div className="p-8 max-w-7xl mx-auto relative z-10" data-testid="ranking-page">
          {/* Back Button */}
          <BackButton to="/dashboard" label="Retour au tableau de bord" className="mb-4" />
          
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mb-8"
          >
            {/* Badge */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-5 ${
              isDark 
                ? 'bg-white/[0.04] border border-white/[0.08]' 
                : 'bg-violet-50 border border-violet-100'
            }`}>
              <ChartLine className="text-violet-500" size={16} weight="fill" />
              <span className={`text-sm font-medium ${isDark ? 'text-white/70' : 'text-violet-700'}`}>
                Suivi SEO en temps réel
              </span>
            </div>
            
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Suivez vos
              <span className="block gradient-text">positions Google</span>
            </h1>
            <p className={`text-lg md:text-xl ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              Surveillez vos mots-clés et optimisez votre visibilité en temps réel
            </p>
          </motion.div>

          {/* Dashboard Stats */}
          {dashboard && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
            >
              <div className={`p-5 rounded-2xl ${isDark ? 'glass-card' : 'bg-white border border-gray-100 shadow-sm'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-gradient-to-br from-violet-500/20 to-pink-500/20' : 'bg-violet-100'}`}>
                    <Target className="text-violet-500" size={24} weight="duotone" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold gradient-text">{dashboard.total_keywords}</p>
                    <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Mots-clés suivis</p>
                  </div>
                </div>
              </div>
              
              <div className={`p-5 rounded-2xl ${isDark ? 'glass-card' : 'bg-white border border-gray-100 shadow-sm'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-amber-500/15' : 'bg-amber-100'}`}>
                    <Trophy className="text-amber-500" size={24} weight="duotone" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-500">{dashboard.top_10}</p>
                    <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Top 10 Google</p>
                  </div>
                </div>
              </div>
              
              <div className={`p-5 rounded-2xl ${isDark ? 'glass-card' : 'bg-white border border-gray-100 shadow-sm'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-emerald-500/15' : 'bg-emerald-100'}`}>
                    <TrendUp className="text-emerald-500" size={24} weight="duotone" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-500">{dashboard.top_30}</p>
                    <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Top 30 Google</p>
                  </div>
                </div>
              </div>
              
              <div className={`p-5 rounded-2xl ${isDark ? 'glass-card' : 'bg-white border border-gray-100 shadow-sm'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/15' : 'bg-blue-100'}`}>
                    <ChartLine className="text-blue-500" size={24} weight="duotone" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-500">#{dashboard.average_position || '-'}</p>
                    <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Position moyenne</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Configuration URL du site */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.15 }}
            className={`p-4 rounded-2xl mb-8 ${isDark ? 'glass-card' : 'bg-white border border-gray-100 shadow-sm'}`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20' : 'bg-blue-100'}`}>
                  <Globe className="text-blue-500" size={20} weight="duotone" />
                </div>
                <div className="flex-1">
                  <Label className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-700'}`}>URL de votre site</Label>
                  <Input
                    type="url"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://monsite.com"
                    className={`mt-1 ${isDark ? 'bg-white/[0.03] border-white/[0.08]' : ''}`}
                    data-testid="website-url-input"
                  />
                </div>
              </div>
              <Button
                onClick={saveWebsiteUrl}
                disabled={savingUrl}
                className="btn-premium h-10"
                data-testid="save-website-url-btn"
              >
                {savingUrl ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
            <p className={`text-xs mt-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
              Cette URL sera utilisée pour détecter votre position dans les résultats Google.
              {trackingStatus?.mode === 'live' && (
                <span className="text-green-500 ml-2">● Mode Live (Serper.dev)</span>
              )}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Ajouter un mot-clé */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-1"
            >
              <div className={`p-6 rounded-2xl ${isDark ? 'glass-card' : 'bg-white border border-gray-100 shadow-sm'}`}>
                <h2 className={`text-lg font-semibold mb-4 flex items-center gap-3 ${isDark ? 'text-white/90' : 'text-gray-900'}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-gradient-to-br from-violet-500/20 to-pink-500/20' : 'bg-violet-100'}`}>
                    <Plus className="text-violet-500" weight="bold" size={20} />
                  </div>
                  Ajouter un mot-clé
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <Label className={isDark ? 'text-white/70' : 'text-gray-700'}>Mot-clé à suivre</Label>
                    <Input
                      placeholder="Ex: plombier Paris"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      className="mt-1"
                      data-testid="keyword-input"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={analyzeKeyword}
                      disabled={!newKeyword.trim() || analyzing}
                      className="flex-1"
                    >
                      <MagnifyingGlass className="mr-1" size={16} />
                      {analyzing ? 'Analyse...' : 'Analyser'}
                    </Button>
                    <Button
                      onClick={addKeyword}
                      disabled={!newKeyword.trim() || addingKeyword}
                      className="flex-1 btn-premium"
                      data-testid="add-keyword-btn"
                    >
                      <Plus className="mr-1" size={16} />
                      {addingKeyword ? 'Ajout...' : 'Suivre'}
                    </Button>
                  </div>
                  
                  {/* Résultat de l'analyse */}
                  <AnimatePresence>
                    {analysis && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className={`p-4 rounded-xl space-y-3 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{analysis.keyword}</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            analysis.analysis.difficulty === 'Facile' || analysis.analysis.difficulty === 'Très facile' 
                              ? 'bg-green-500/20 text-green-500' 
                              : analysis.analysis.difficulty === 'Moyen' 
                                ? 'bg-yellow-500/20 text-yellow-500' 
                                : 'bg-red-500/20 text-red-500'
                          }`}>
                            {analysis.analysis.difficulty}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div className={`p-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                            <p className={isDark ? 'text-white/50' : 'text-gray-500'}>Difficulté</p>
                            <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{analysis.analysis.difficulty_score}/100</p>
                          </div>
                          <div className={`p-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                            <p className={isDark ? 'text-white/50' : 'text-gray-500'}>Volume estimé</p>
                            <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{analysis.analysis.estimated_volume?.toLocaleString()}/mois</p>
                          </div>
                          {analysis.analysis.estimated_cpc && (
                            <div className={`p-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                              <p className={isDark ? 'text-white/50' : 'text-gray-500'}>CPC estimé</p>
                              <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{analysis.analysis.estimated_cpc}€</p>
                            </div>
                          )}
                          {analysis.analysis.opportunity_score && (
                            <div className={`p-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white'}`}>
                              <p className={isDark ? 'text-white/50' : 'text-gray-500'}>Opportunité</p>
                              <p className={`font-bold ${analysis.analysis.opportunity_score > 70 ? 'text-green-500' : analysis.analysis.opportunity_score > 50 ? 'text-yellow-500' : 'text-red-500'}`}>
                                {analysis.analysis.opportunity_score}/100
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {analysis.has_existing_content && (
                          <p className="text-xs text-green-500 flex items-center gap-1">
                            <Lightning weight="fill" />
                            Vous avez déjà du contenu pour ce mot-clé !
                          </p>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                
                {/* Améliorations récentes */}
                {dashboard?.recent_improvements?.length > 0 && (
                  <div className={`mt-6 pt-6 ${isDark ? 'border-t border-white/10' : 'border-t border-gray-200'}`}>
                    <h3 className={`text-sm font-medium mb-3 flex items-center gap-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                      <TrendUp className="text-green-500" size={16} />
                      Améliorations récentes
                    </h3>
                    <div className="space-y-2">
                      {dashboard.recent_improvements.map((imp, i) => (
                        <div key={i} className={`flex items-center justify-between text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                          <span className="truncate flex-1">{imp.keyword}</span>
                          <span className="text-green-500 flex items-center gap-1 font-medium">
                            <ArrowUp size={12} weight="bold" />
                            +{imp.improvement}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Liste des mots-clés */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="lg:col-span-2"
            >
              <div className={`p-6 rounded-2xl ${isDark ? 'glass-card' : 'bg-white border border-gray-100 shadow-sm'}`}>
                <h2 className={`text-lg font-semibold mb-4 flex items-center gap-3 ${isDark ? 'text-white/90' : 'text-gray-900'}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-gradient-to-br from-violet-500/20 to-pink-500/20' : 'bg-violet-100'}`}>
                    <ChartLine className="text-violet-500" weight="duotone" size={20} />
                  </div>
                  Mots-clés suivis ({keywords.length})
                </h2>
                
                {keywords.length === 0 ? (
                  <div className="text-center py-12">
                    <Target size={48} className={`mx-auto mb-4 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                    <p className={isDark ? 'text-white/50' : 'text-gray-500'}>Aucun mot-clé suivi</p>
                    <p className={`text-sm ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                      Ajoutez votre premier mot-clé pour commencer le suivi
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {keywords.map((kw) => (
                      <div
                        key={kw.keyword_id}
                        className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-colors ${
                          isDark 
                            ? 'bg-white/[0.03] hover:bg-white/[0.06]' 
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                        onClick={() => viewKeywordDetails(kw.keyword_id)}
                      >
                        <PositionBadge position={kw.current_position} />
                        
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{kw.keyword}</p>
                          <div className={`flex items-center gap-3 text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                            <span>Meilleur: #{kw.best_position}</span>
                            {kw.has_seo_page && (
                              <span className="text-green-500">Page SEO liée</span>
                            )}
                          </div>
                        </div>
                        
                        <MiniChart history={kw.positions_history} isDark={isDark} />
                        
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              refreshKeyword(kw.keyword_id);
                            }}
                            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-white/50 hover:text-violet-400' : 'hover:bg-gray-200 text-gray-400 hover:text-violet-500'}`}
                            title="Rafraîchir"
                          >
                            <ArrowsClockwise size={18} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteKeyword(kw.keyword_id);
                            }}
                            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-white/50 hover:text-red-400' : 'hover:bg-gray-200 text-gray-400 hover:text-red-500'}`}
                            title="Supprimer"
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Modal détails mot-clé */}
          <AnimatePresence>
            {selectedKeyword && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={() => setSelectedKeyword(null)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className={`rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto ${
                    isDark ? 'glass-card' : 'bg-white shadow-xl'
                  }`}
                  onClick={(e) => e.stopPropagation()}
                  data-testid="keyword-detail-modal"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedKeyword.keyword}</h3>
                    <PositionBadge position={selectedKeyword.current_position} />
                  </div>
                  
                  {/* Position Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className={`text-center p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>#{selectedKeyword.current_position}</p>
                      <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Actuelle</p>
                    </div>
                    <div className={`text-center p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                      <p className="text-2xl font-bold text-green-500">#{selectedKeyword.best_position}</p>
                      <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Meilleure</p>
                    </div>
                    <div className={`text-center p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedKeyword.difficulty_analysis?.difficulty_score}</p>
                      <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Difficulté</p>
                    </div>
                  </div>
                  
                  {/* Traffic Estimate */}
                  {selectedKeyword.traffic_estimate && (
                    <div className={`p-4 rounded-xl mb-6 ${isDark ? 'bg-gradient-to-r from-violet-500/10 to-pink-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-100'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Users className="text-violet-500" size={18} />
                        <h4 className={`text-sm font-semibold ${isDark ? 'text-white/90' : 'text-gray-800'}`}>Estimation de trafic</h4>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div>
                          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {selectedKeyword.traffic_estimate.estimated_monthly_clicks?.toLocaleString() || 0}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Clics/mois</p>
                        </div>
                        <div>
                          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {selectedKeyword.traffic_estimate.ctr}%
                          </p>
                          <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>CTR estimé</p>
                        </div>
                        <div>
                          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            ~{selectedKeyword.traffic_estimate.estimated_daily_clicks || 0}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Clics/jour</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Trend */}
                  {selectedKeyword.trend && (
                    <div className={`flex items-center justify-between p-3 rounded-xl mb-6 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                      <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>Tendance (7 jours)</span>
                      <span className={`flex items-center gap-1 font-medium ${
                        selectedKeyword.trend.direction === 'up' ? 'text-green-500' : 
                        selectedKeyword.trend.direction === 'down' ? 'text-red-500' : 
                        isDark ? 'text-white/50' : 'text-gray-500'
                      }`}>
                        {selectedKeyword.trend.direction === 'up' ? (
                          <>
                            <ArrowUp weight="bold" size={14} />
                            +{Math.abs(selectedKeyword.trend.change)} places
                          </>
                        ) : selectedKeyword.trend.direction === 'down' ? (
                          <>
                            <ArrowDown weight="bold" size={14} />
                            {Math.abs(selectedKeyword.trend.change)} places
                          </>
                        ) : (
                          <>
                            <Minus size={14} />
                            Stable
                          </>
                        )}
                      </span>
                    </div>
                  )}
                  
                  {selectedKeyword.recommendations?.length > 0 && (
                    <div className="mb-6">
                      <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>Recommandations</h4>
                      <ul className="space-y-2">
                        {selectedKeyword.recommendations.map((rec, i) => (
                          <li key={i} className={`flex items-start gap-2 text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                            <Lightning className="text-violet-500 mt-0.5 flex-shrink-0" size={14} />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <Button
                    className="w-full btn-premium"
                    onClick={() => setSelectedKeyword(null)}
                    data-testid="close-keyword-modal"
                  >
                    Fermer
                  </Button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </PageTransition>
    </AppLayout>
  );
}
